import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { MenuProps } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  ApartmentOutlined,
  BankOutlined,
  ExperimentOutlined,
  IdcardOutlined,
  LogoutOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { IconShieldLock } from '@tabler/icons-react';

import { PermissionKey } from '@evimo/types/enums/permission-key';
import { SystemRoleName } from '@evimo/types/enums/system-role-name';
import type { CurrentUser } from '@evimo/types';
import { usePermission } from '~/app/auth/hooks/usePermission';

export function useLandingAccountMenu(
  user: CurrentUser | null,
  logout: () => Promise<void>,
) {
  const navigate = useNavigate();
  const { t } = useTranslation('navigation');
  const { hasPermission } = usePermission();

  const accountMenu: MenuProps['items'] = useMemo(() => {
    const items: NonNullable<MenuProps['items']> = [
      {
        key: 'profile',
        icon: <IdcardOutlined />,
        label: t('account.profile'),
        onClick: () => {
          navigate('/account/profile');
        },
      },
    ];

    if (hasPermission(PermissionKey.ROLES_READ)) {
      items.push({
        key: 'roles',
        icon: <IconShieldLock size={16} />,
        label: t('rolesAndPermissions'),
        onClick: () => {
          navigate('/admin/roles');
        },
      });
    }

    if (hasPermission(PermissionKey.PIPELINE_DEBUG_RUN)) {
      items.push({
        key: 'pipeline-debug',
        icon: <ExperimentOutlined />,
        label: t('pipelineDebug'),
        onClick: () => {
          navigate('/admin/pipeline-debug');
        },
      });
    }

    if (user?.roleName === SystemRoleName.SUPER_ADMIN) {
      items.push({
        key: 'backoffice',
        icon: <BankOutlined />,
        label: t('account.backOffice'),
        onClick: () => {
          navigate('/admin/back-office');
        },
      });
      items.push({
        key: 'settings-center',
        icon: <SettingOutlined />,
        label: t('account.settingsCenter'),
        onClick: () => {
          navigate('/admin/back-office/settings');
        },
      });
      items.push({
        key: 'organization',
        icon: <ApartmentOutlined />,
        label: t('account.organizationRelationship'),
        onClick: () => {
          navigate('/admin/back-office/organization-relationship');
        },
      });
    }

    items.push({ type: 'divider' });
    items.push({
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('account.logout'),
      onClick: async () => {
        await logout();
        navigate('/login');
      },
    });

    return items;
  }, [logout, navigate, user?.roleName, hasPermission, t]);

  return accountMenu;
}
